// client/src/context/AuthContext.js
//Este archivo manejará todo el flujo de sesión (inicio, cierre, persistencia del usuario y rol).
import { createContext, useContext, useEffect, useState } from "react";
import { login, logout, verificarSesion } from "../api/usuarios.js";
import { loginSuperAdmin } from "../api/superAdmin.js";

const AuthContext = createContext();

// Helper para validar si el JWT aún no ha expirado
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonPayload);
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return false; // Token expirado
    }
    return true;
  } catch (e) {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  // Inicialización síncrona para evitar que ProtectedRoute redirija a /login en el primer render al recargar la página
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken && isTokenValid(storedToken)) {
      return storedToken;
    }
    if (storedToken) {
      localStorage.removeItem("token");
      localStorage.removeItem("usuario");
      localStorage.removeItem("dashboard_modulo_activo");
    }
    return null;
  });

  const [usuario, setUsuario] = useState(() => {
    try {
      const storedUser = localStorage.getItem("usuario");
      const storedToken = localStorage.getItem("token");
      if (storedUser && storedToken && isTokenValid(storedToken)) {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      console.error("Error al restaurar sesión de usuario:", e);
    }
    return null;
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Verificación en segundo plano con el servidor vía Cookie HttpOnly
    const verificar = async () => {
      setLoading(true);
      try {
        const res = await verificarSesion();
        if (res && res.usuario) {
          setUsuario(res.usuario);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("usuario");
          localStorage.removeItem("dashboard_modulo_activo");
          setUsuario(null);
          setToken(null);
        }
      } finally {
        setLoading(false);
      }
    };

    if (usuario || token) {
      verificar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login - Intenta primero como Usuario, luego como SuperAdmin
  const iniciarSesion = async (credenciales) => {
    try {
      // Intentar login como Usuario normal
      const { token, usuario } = await login(credenciales);
      setToken(token);
      setUsuario(usuario);
      localStorage.setItem("token", token);
      localStorage.setItem("usuario", JSON.stringify(usuario));
      return usuario;
    } catch (err) {
      // Si falla, intentar como SuperAdmin
      try {
        // Adaptar credenciales para SuperAdmin (usa "contraseña" con ñ)
        const credencialesSuperAdmin = {
          correo: credenciales.correo,
          contraseña: credenciales.contrasenia || credenciales.contraseña
        };
        
        const { token, superAdmin } = await loginSuperAdmin(credencialesSuperAdmin);
        // Agregar una propiedad para identificar que es SuperAdmin y su rol
        const usuarioSuperAdmin = { ...superAdmin, isSuperAdmin: true, rol: "superAdmin" };
        setToken(token);
        setUsuario(usuarioSuperAdmin);
        localStorage.setItem("token", token);
        localStorage.setItem("usuario", JSON.stringify(usuarioSuperAdmin));
        return usuarioSuperAdmin;
      } catch (superAdminErr) {
        throw err.response?.data?.message || "Error al iniciar sesión";
      }
    }
  };

  // Logout seguro en servidor y cliente
  const cerrarSesion = async () => {
    // 1. Limpieza inmediata y síncrona en cliente para evitar condiciones de carrera al recargar
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    localStorage.removeItem("dashboard_modulo_activo");
    setToken(null);
    setUsuario(null);

    // 2. Notificación al servidor para limpiar cookies HttpOnly
    try {
      await logout();
    } catch (err) {
      console.warn("Aviso al cerrar sesión en servidor:", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        usuario,
        token,
        loading,
        iniciarSesion,
        cerrarSesion,
        isAuthenticated: !!usuario && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth debe usarse dentro de un AuthProvider");
  return context;
};
